import {getPresignedUrl, putFile} from "../api/aws";
import {HttpVerbs, StatusTypes} from "../definitions/enums";
import {ChangeEvent, useEffect, useRef, useState} from "react";
import {useAppDispatch, useArtwork} from "../redux/stateHooks";
import {nanoid} from "@reduxjs/toolkit";
import {identity, sanitize} from "../utils/helpers";
import {useMutation} from "@apollo/client";
import {
    clearArtworkFile,
    clearArtworkNotes,
    setArtwork,
    setArtworkNotesPending,
    setArtworkUploadPending
} from "../redux/slices/addToCart";
import {contentService} from "../api/contentService";
import {useCustomerArtworkModal} from "./useCustomerArtworkModal";

export const useContentUpload = () => {
    const dispatch = useAppDispatch();
    const artwork = useArtwork();
    const [status, setStatus] = useState<StatusTypes>(StatusTypes.Default);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {model: {show, toggle}} = useCustomerArtworkModal();
    const {isAuthenticated, accountId} = identity();

    // Create a preview URL for the file if it exists and is not empty (the user has selected a file)
    useEffect(() => {
        // If there is already a preview, revoke it to prevent memory leaks as the user selects a new file
        if (artwork?.preview) URL.revokeObjectURL(artwork.preview);

        // If there is no file, return early as the preview was already revoked above, and there is no file to preview
        if (!artwork?.file) return;
        
        // Create a preview URL for the file and set it to the preview state
        dispatch(setArtwork({
            preview: artwork.file.type.startsWith('image') ? URL.createObjectURL(artwork.file) : undefined, // Only create a preview URL for image files
            file: artwork.file
        }));

        // On unmounting, clean up if there is a preview, revoke it to prevent memory leaks
        return () => {
            if (artwork.preview) URL.revokeObjectURL(artwork.preview);
        };
    }, [artwork?.file]);

    const [createArtworkAsync, {
        error: apolloError,
        reset: apolloReset
    }] = useMutation(contentService.artwork.create, {
        errorPolicy: "all"
    });

    // If mutation resulted in an error, set shared error state
    useEffect(() => {
        if (!apolloError) return;
        setStatus(StatusTypes.Error);
    }, [apolloError]);

    const uploadArtwork = async (file: File) => {
        // Set artwork upload pending state to true
        dispatch(setArtworkUploadPending(true));

        // Reset apollo state on new upload attempt
        apolloReset();

        // Set status to loading to display loading indicator
        setStatus(StatusTypes.Loading);

        const sanitizeFilename = sanitize(file.name).toLowerCase();
        const fileExtension = sanitizeFilename.split('.').pop();

        // If a file extension cannot be determined, set error state and return
        if (!fileExtension) {
            setStatus(StatusTypes.Error);
            dispatch(setArtworkUploadPending(false));
            return;
        }

        const storageId = `CustUploads/${nanoid()}.${fileExtension}`;
        const temporaryKey = `customerartwork/${nanoid()}/images/${sanitizeFilename}`;

        const fileMetadata = {
            'x-amz-meta-content-type': 'customerartwork',
            'x-amz-meta-destination-bucket': 3,
            'x-amz-meta-destination-filename': storageId
        };

        // Create presigned URL for upload to S3 bucket
        const {Url, errorMessage} = await getPresignedUrl(HttpVerbs.PUT, temporaryKey, undefined, fileMetadata);

        // If presigned URL creation failed, set error state and return
        if (errorMessage) {
            setStatus(StatusTypes.Error);
            dispatch(setArtworkUploadPending(false));
            return;
        }

        // Upload file to S3 bucket using presigned URL and file from input element state (file)
        const uploadStatusCode = await putFile({
            presignedUrl: Url,
            file: file,
            metadata: {
                'Content-Type': fileExtension === 'psd' ? 'image/vnd.adobe.photoshop' : file.type,
                'x-amz-acl': "bucket-owner-full-control",
                ...fileMetadata
            }
        });

        // If upload failed, set error state and return
        if (uploadStatusCode !== 200) {
            setStatus(StatusTypes.Error);
            dispatch(setArtworkUploadPending(false));
            return;
        }

        // Create artwork record in a database
        const {data} = await createArtworkAsync({
            variables: {
                filename: file.name,
                storageId: storageId,
                notes: artwork?.notes,
                accountId: isAuthenticated ? accountId : undefined
            }
        });

        // Parse the artwork id from the response
        const {id} = data?.payload?.artwork || {id: 0};

        // If database record creation failed, set error state and return
        if (id === 0) {
            setStatus(StatusTypes.Error);
            dispatch(setArtworkUploadPending(false));
            return;
        }

        // Add artwork to redux state
        dispatch(setArtwork({
            id: id,
            name: file.name
        }));

        // Set status state to success to display success indicator
        setStatus(StatusTypes.Success);

        // Toggle the modal to close it
        toggle();

        // Set artwork upload pending state to false
        dispatch(setArtworkUploadPending(false));
    };

    const uploadArtworkNotes = async () => {
        // Set artwork upload pending state to true
        dispatch(setArtworkNotesPending(true));

        // Reset apollo state on new upload attempt
        apolloReset();

        // Set status to loading to display loading indicator
        setStatus(StatusTypes.Loading);

        // Create artwork record in a database
        const {data} = await createArtworkAsync({
            variables: {
                notes: artwork?.notes,
                accountId: accountId
            }
        });

        // Parse the artwork id from the response
        const {id} = data?.payload?.artwork || {id: 0};

        // If database record creation failed, set error state and return
        if (id === 0) {
            setStatus(StatusTypes.Error);
            dispatch(setArtworkUploadPending(false));
            return;
        }

        // Add artwork to redux state
        dispatch(setArtwork({
            id: id,
            notes: artwork?.notes
        }));

        // Set status state to success to display success indicator
        setStatus(StatusTypes.Success);

        // Toggle the modal to close it
        toggle();

        // Set artwork upload pending state to false
        dispatch(setArtworkNotesPending(false));

        return data?.payload?.artwork?.id;
    };

    return {
        status,
        isLoading: status === StatusTypes.Loading,
        isError: status === StatusTypes.Error,
        isSuccess: status === StatusTypes.Success,
        canUpload: !!(artwork?.file && status === StatusTypes.Default),
        onChange: async ({target: {files: fileList}}: ChangeEvent<HTMLInputElement>) => {
            if (!fileList) return;
            let file = fileList[0];
            dispatch(setArtwork({file: file}));
            if ((file))
                await uploadArtwork(file);
        },
        uploadArtworkNotes,
        reset: () => {
            // Reset file input element state on dismissing, if it exists
            if (fileInputRef.current) fileInputRef.current.value = '';

            // Reset file state on dismissing
            dispatch(setArtwork({file: undefined}));

            // Reset apollo state on dismissing
            apolloReset();

            // Reset redux state on dismissing
            dispatch(clearArtworkFile());
            dispatch(clearArtworkNotes());

            // Reset status state on dismissing
            setStatus(StatusTypes.Default);
        },
        input: {
            ref: fileInputRef,
            file: artwork?.file,
            preview: artwork?.preview
        },
        modal: {
            show: status == StatusTypes.Loading ? true : show,
            toggle: status == StatusTypes.Loading ? undefined : toggle
        }
    };
};